import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import PhotosViewer from "../components/PhotosViewer";
import { fmtWorkCenter } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { fetchBox } from "../services/boxDetailApi";
import type { Box } from "../types/boxes";

/**
 * Dedicated /photos/:scuName route. Lives off the SmartBoxes-page
 * "photos →" link so an operator can drill into a box's camera
 * history without scrolling past the inventory list (Jason
 * 2026-06-18 — Inventory detail no longer carries this).
 *
 * Pure wrapper around <PhotosViewer> with a small header that
 * gives breadcrumb context (smartbox code + workcenter + a back
 * link to /boxes). The viewer owns the data flow + the two view
 * modes (events vs photos).
 */
export const BoxPhotosPage: React.FC = () => {
  const { scuName = "" } = useParams<{ scuName: string }>();
  const { token } = useAuth();

  // Fetch just this box for the header breadcrumb instead of the whole fleet.
  const [box, setBox] = useState<Box | null>(null);
  useEffect(() => {
    if (!scuName || !token) return;
    let cancelled = false;
    fetchBox(scuName, token)
      .then((b) => !cancelled && setBox(b))
      .catch(() => !cancelled && setBox(null));
    return () => {
      cancelled = true;
    };
  }, [scuName, token]);

  return (
    <div className="p-4 md:p-6">
      <header className="mb-3 flex flex-wrap items-baseline gap-3">
        <Link
          to="/boxes"
          className="text-xs text-secondary-500 hover:text-secondary-800"
        >
          ← SmartBoxes
        </Link>
        <h1 className="text-xl font-bold">
          Photos · {box?.smartbox_code ?? scuName}
        </h1>
        {box?.work_center && (
          <span className="text-secondary-500 text-sm">
            {fmtWorkCenter(box.work_center)}
          </span>
        )}
        {box?.customer && (
          <span className="text-secondary-500 text-sm">· {box.customer}</span>
        )}
      </header>

      <PhotosViewer scu={scuName} />
    </div>
  );
};

export default BoxPhotosPage;
